import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { RedesignFonts, COMIC_BOOK, C } from '../kit';
import { SwampMap } from './Map';
import { 
  M5Header, M5About1, M5About2, M5Takeover1, M5Takeover2, M5Tokenomics1, M5Tokenomics2, 
  M5Dollarize1, M5Dollarize2, M5Roadmap1, M5Roadmap2, M5Artist1, M5Artist2, 
  M5Nfts1, M5Nfts2, M5Memes, M5Comic, M5Join 
} from './Sections';
import { ComicReader } from '../comic/ComicReader';

export default function RedesignM6() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeChapter, setActiveChapter] = useState(0);
  const [comicOpen, setComicOpen] = useState(false);
  const [totalChapters, setTotalChapters] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sections = Array.from(containerRef.current?.querySelectorAll('.chapter') || []);
            const index = sections.indexOf(entry.target);
            if (index !== -1) setActiveChapter(index);
          }
        });
      },
      { threshold: 0.5 }
    );

    const sections = containerRef.current?.querySelectorAll('.chapter');
    setTotalChapters(sections?.length || 0);
    sections?.forEach((s) => observer.observe(s));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Container ref={containerRef}>
      <RedesignFonts />
      <ProgressBar active={activeChapter} total={totalChapters} />
      
      <M5Header />
      <SwampMap onSelect={(id) => scrollToSection(id)} />
      
      <M5About1 />
      <M5About2 />
      
      <M5Takeover1 />
      <M5Takeover2 />
      
      <M5Tokenomics1 />
      <M5Tokenomics2 />
      
      <M5Dollarize1 />
      <M5Dollarize2 />
      
      <M5Roadmap1 />
      <M5Roadmap2 />
      
      <M5Artist1 />
      <M5Artist2 />
      
      <M5Nfts1 />
      <M5Nfts2 />
      
      <M5Memes />
      <M5Comic openComic={() => setComicOpen(true)} />
      
      <M5Join />

      {comicOpen && (
        <ComicReader 
          pages={COMIC_BOOK} 
          startPage={0} 
          pdf="/zeus.pdf" 
          onClose={() => setComicOpen(false)} 
        />
      )}
    </Container>
  );
}

const Container = styled.div`
  font-family: 'Ubuntu', sans-serif;
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  background: ${C.ink};
  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
  position: relative;
`;

const ProgressWrap = styled.div`
  position: fixed;
  top: max(8px, env(safe-area-inset-top));
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 430px;
  height: 6px;
  display: flex;
  gap: 4px;
  z-index: 9999;
  padding: 10px 10px 0;
  pointer-events: none;
  box-sizing: border-box;
`;

const ProgressSeg = styled.div<{ $active: boolean }>`
  flex: 1;
  background: ${p => p.$active ? C.yellow : 'rgba(255,255,255,0.8)'};
  border: 1px solid ${C.ink};
  box-shadow: 1px 1px 0 rgba(14,27,51,0.5);
  border-radius: 3px;
  transition: background 0.3s;
`;

const ProgressBar = ({ active, total }: { active: number, total: number }) => (
  <ProgressWrap>
    {Array.from({ length: total }).map((_, i) => (
      <ProgressSeg key={i} $active={i <= active} />
    ))}
  </ProgressWrap>
);
